import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Navigation, AlertCircle, Eye, EyeOff, Minimize2, Maximize2, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { LocationTracker, updateUserLocation, setLocationSharingForJob, updateJobLocation, type LocationData } from '@/utils/locationTracker';
import { TrackingMap } from './TrackingMap';
import { supabase } from '@/integrations/supabase/client';

interface LocationControlsProps {
  userType: 'customer' | 'tradesman';
  activeJobId?: string;
  onLocationUpdate?: (location: LocationData) => void;
  onClose?: () => void;
}

export const LocationControls: React.FC<LocationControlsProps> = ({
  userType,
  activeJobId,
  onLocationUpdate,
  onClose,
}) => {
  const [isTracking, setIsTracking] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(null);
  const [locationShared, setLocationShared] = useState(false);
  const [isMinimized, setIsMinimized] = useState(() => {
    const saved = localStorage.getItem('locationWidget_minimized');
    return saved ? JSON.parse(saved) : false;
  });
  const [userLocationChoice, setUserLocationChoice] = useState(() => {
    const saved = localStorage.getItem('userLocationChoice');
    return saved ? JSON.parse(saved) : null; // null = not set, true = allowed, false = denied
  });
  const [locationTracker] = useState(() => new LocationTracker());
  const { toast } = useToast();

  // Save minimized state to localStorage
  useEffect(() => {
    localStorage.setItem('locationWidget_minimized', JSON.stringify(isMinimized));
  }, [isMinimized]);

  // Save user location choice to localStorage
  useEffect(() => {
    if (userLocationChoice !== null) {
      localStorage.setItem('userLocationChoice', JSON.stringify(userLocationChoice));
    }
  }, [userLocationChoice]);

  // Auto-start tracking if user previously allowed
  useEffect(() => {
    if (userLocationChoice === true && !isTracking) {
      startTracking();
    }
  }, [userLocationChoice]);

  useEffect(() => {
    return () => {
      locationTracker.stopTracking();
    };
  }, [locationTracker]);

  const handleLocationUpdate = async (location: LocationData) => {
    console.log('LocationControls: Location update received:', location);
    setCurrentLocation(location);
    onLocationUpdate?.(location);

    try {
      console.log('LocationControls: Updating location in database...');
      // Update location in database
      await updateUserLocation(location);
      
      // For customers with active jobs, automatically share location and update job location
      if (userType === 'customer' && activeJobId) {
        console.log('LocationControls: Customer with active job - updating job location and sharing...');
        
        // Update the job location to current location
        await updateJobLocation(activeJobId, location);
        
        // Automatically share location with tradesman
        await setLocationSharingForJob(activeJobId, location);
        setLocationShared(true);
      }
      
      console.log('LocationControls: Location update completed successfully');
    } catch (error) {
      console.error('LocationControls: Error updating location:', error);
    }
  };

  const startTracking = async () => {
    try {
      console.log('LocationControls: Starting tracking for user type:', userType, 'activeJobId:', activeJobId);
      const success = await locationTracker.startTracking(handleLocationUpdate);
      console.log('LocationControls: Tracking start result:', success);
      
      if (success) {
        setIsTracking(true);
        setUserLocationChoice(true); // Save user's choice to allow location
        
        if (userType === 'customer' && activeJobId) {
          setLocationShared(true); // Automatically enable sharing for customers
          toast({
            title: 'Location tracking started',
            description: 'Your live location is now the job location and shared with the tradesman',
          });
        } else {
          toast({
            title: 'Location tracking started',
            description: 'Your location is now being tracked',
          });
        }
      } else {
        throw new Error('Failed to start location tracking');
      }
    } catch (error) {
      console.error('LocationControls: Error starting tracking:', error);
      setUserLocationChoice(false); // Save user's choice to deny location
      toast({
        title: 'Location access denied',
        description: 'Please enable location services to use this feature',
        variant: 'destructive',
      });
    }
  };

  const stopTracking = async () => {
    await locationTracker.stopTracking();
    setIsTracking(false);
    setLocationShared(false);
    toast({
      title: 'Location tracking stopped',
      description: 'Your location is no longer being tracked',
    });
  };

  const shareLocationWithTradesman = async () => {
    if (!currentLocation || !activeJobId) {
      toast({
        title: 'Cannot share location',
        description: 'No current location or active job found',
        variant: 'destructive',
      });
      return;
    }

    try {
      await setLocationSharingForJob(activeJobId, currentLocation);
      setLocationShared(true);
      toast({
        title: 'Location shared',
        description: 'Your location has been shared with the tradesman',
      });
    } catch (error) {
      console.error('Location sharing error:', error);
      toast({
        title: 'Error sharing location',
        description: error instanceof Error ? error.message : 'Failed to share location with tradesman',
        variant: 'destructive',
      });
    }
  };

  const stopSharingLocation = async () => {
    if (!activeJobId) return;

    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('job_location_sharing')
        .update({ is_location_shared: false })
        .eq('job_id', activeJobId);

      if (error) throw error;

      setLocationShared(false);
      toast({
        title: 'Location sharing stopped',
        description: 'Your location is no longer shared with the tradesman',
      });
    } catch (error) {
      toast({
        title: 'Error stopping location sharing',
        description: 'Failed to stop sharing location',
        variant: 'destructive',
      });
    }
  };

  // Minimized state - show small floating button
  if (isMinimized) {
    // Minimized UI hidden to avoid overlay on maps
    return null;
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Location Services
          </div>
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8 -mr-2 rounded-full hover:bg-destructive/10 hover:text-destructive"
              aria-label="Close location services"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
        </CardTitle>
        <CardDescription>
          {userType === 'customer' 
            ? 'Your live location will become the job location and be shared with tradesmen automatically'
            : 'Enable location tracking so customers can see your arrival progress. This helps build trust and keeps customers informed.'
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1">
        {/* Current Location Status */}
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Navigation className={`h-4 w-4 ${isTracking ? 'text-green-600' : 'text-muted-foreground'}`} />
            <span className="text-sm font-medium">
              {isTracking ? 'Location Active' : 'Location Inactive'}
            </span>
          </div>
          <Badge variant={isTracking ? "default" : "secondary"}>
            {isTracking ? 'Tracking' : 'Stopped'}
          </Badge>
        </div>

        {/* Location Controls */}
        <div className="flex gap-2">
          {!isTracking ? (
            <Button onClick={startTracking} variant="hero" className="flex-1">
              <MapPin className="h-4 w-4 mr-2" />
              {userType === 'tradesman' ? 'Share My Location with Customer' : 'Enable Location'}
            </Button>
          ) : (
            <Button onClick={stopTracking} variant="outline" className="flex-1">
              <MapPin className="h-4 w-4 mr-2" />
              Stop Tracking
            </Button>
          )}
        </div>

        {/* Customer-specific location sharing */}
        {userType === 'customer' && isTracking && activeJobId && (
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-accent-orange" />
              <span className="text-sm font-medium">Job Location Sharing</span>
            </div>
            
            {!locationShared ? (
              <Button 
                onClick={shareLocationWithTradesman} 
                variant="orange" 
                className="w-full"
                disabled={!currentLocation}
              >
                <Eye className="h-4 w-4 mr-2" />
                Share Location with Tradesman
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <Eye className="h-4 w-4" />
                  <span>Location shared with tradesman</span>
                </div>
                <Button 
                  onClick={stopSharingLocation} 
                  variant="outline" 
                  size="sm"
                  className="w-full"
                >
                  <EyeOff className="h-4 w-4 mr-2" />
                  Stop Sharing
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Current location display */}
        {currentLocation && (
          <div className="text-xs text-muted-foreground space-y-1">
            <div>Lat: {currentLocation.latitude.toFixed(6)}</div>
            <div>Lng: {currentLocation.longitude.toFixed(6)}</div>
            {currentLocation.accuracy && (
              <div>Accuracy: ±{Math.round(currentLocation.accuracy)}m</div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};